import type { PublisherManifest } from '@fcbh/schema';

/**
 * Apply a partner's brand override to the Reader chrome.
 *
 * The Reader's `--accent` CSS var drives every cobalt accent in the UI
 * (verse numbers, links, focus rings, the player surface). Replacing it
 * at boot means every accent automatically tracks the partner without
 * touching a single component.
 *
 * We inject a single `<style>` tag at the end of `<head>` so it wins over
 * the `:root` rule in styles.css. The element is reused if called twice
 * (cheap re-paint on theme change).
 */
const STYLE_ID = 'fcbh-publisher-brand';

export function applyPublisherBrand(manifest: PublisherManifest): void {
  if (typeof document === 'undefined') return;
  const accent = manifest.brand?.accent;
  if (!accent) return;
  const safe = /^#[0-9A-Fa-f]{6}$/.test(accent) ? accent : null;
  if (!safe) return;
  const existing = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  const tag = existing ?? document.createElement('style');
  tag.id = STYLE_ID;
  // Override the four cobalt-derived tokens so the whole UI swaps accent.
  // We don't touch surface/ink/hair — those define the "is this dark or
  // light parchment" baseline, which the partner shouldn't tune through
  // this surface.
  tag.textContent = `
    :root,
    [data-theme='dark'] {
      --accent: ${safe};
      --accent-2: ${safe};
      --accent-bg: color-mix(in oklab, ${safe} 12%, transparent);
      --accent-soft: color-mix(in oklab, ${safe} 18%, transparent);
    }
  `;
  if (!existing) document.head.appendChild(tag);
}
